import type { FileUploadFileAcceptDetails } from "@mobsuccess-devops/react-ui/FileUpload";

declare global {
  // this is a declaration merging, we cannot use `type` here as it would be a new declaration
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface File {
    metadata:
      | { loaded?: false }
      | ({ loaded: true; base64: string } & (
          | {
              type: "image";
              width: number;
              height: number;
            }
          | {
              type: "video";
              width: number;
              height: number;
              duration: number;
              byterate: number;
            }
          | {
              type: "zip";
              width: number;
              height: number;
            }
          | {
              type: "audio";
              duration: number;
              width?: never;
              height?: never;
            }
        ));
  }
}

// Guarded: `File` is the worker's global, and under shared test workers
// (isolate: false) this module is re-evaluated for every test file — a second
// unguarded `defineProperty` on a non-configurable property throws.
if (!Object.getOwnPropertyDescriptor(File.prototype, "metadata")) {
  Object.defineProperty(File.prototype, "metadata", {
    writable: true,
    value: { loaded: false },
  });
}

export const sizeUnits = {
  kilobyte: 10 ** 3,
  kibibyte: 2 ** 10,
  megabyte: 10 ** 6,
  mebibyte: 2 ** 20,
  gigabyte: 10 ** 9,
  gibibyte: 2 ** 30,
};

export function toSizeString(bytessize: number): string {
  if (bytessize < sizeUnits.kilobyte) {
    return `${bytessize}bytes`;
  }
  if (bytessize < sizeUnits.megabyte) {
    return `${(bytessize / sizeUnits.kilobyte).toFixed(1)}Kb`;
  }
  if (bytessize < sizeUnits.gigabyte) {
    return `${(bytessize / sizeUnits.megabyte).toFixed(1)}Mb`;
  }
  return `${(bytessize / sizeUnits.gigabyte).toFixed(1)}Gb`;
}

function getBase64(file: File): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(reader.result ? reader.result.toString() : null);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export async function loadImageDimensions(
  file: File,
): Promise<Omit<File["metadata"], "loaded">> {
  if (!file.type.startsWith("image/")) {
    throw new Error("File is not an image");
  }
  if (file.metadata.loaded && file.metadata.type === "image") {
    const { type, base64, width, height } = file.metadata;
    return { type, base64, width, height };
  }
  const base64 = await getBase64(file);

  if (!base64) {
    throw new Error("Could not read file");
  }

  const { width, height } = await getImageSize(file);
  const metadata = {
    type: "image",
    base64: base64.toString(),
    width,
    height,
  } as const;

  file.metadata = { ...metadata, loaded: true };
  return metadata;
}

function getVideoDuration(
  blob: Blob,
): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onerror = reject;

    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });
    };

    video.src = URL.createObjectURL(blob);
  });
}

function getAudioDuration(blob: Blob): Promise<{ duration: number }> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");
    audio.preload = "metadata";

    audio.onerror = reject;

    audio.onloadedmetadata = function () {
      window.URL.revokeObjectURL(audio.src);
      resolve({
        duration: audio.duration,
      });
    };

    audio.src = URL.createObjectURL(blob);
  });
}

export async function loadVideoDuration(
  file: File,
): Promise<Omit<File["metadata"], "loaded">> {
  if (!file.type.startsWith("video/")) {
    throw new Error("File is not a video");
  }
  if (file.metadata.loaded && file.metadata.type === "video") {
    const { type, base64, width, height, duration, byterate } = file.metadata;
    return { type, base64, width, height, duration, byterate };
  }
  const base64 = await getBase64(file);
  if (!base64) {
    throw new Error("Could not read file");
  }
  const fetchVideo = await fetch(base64);
  const { duration, width, height } = await getVideoDuration(
    await fetchVideo.blob(),
  );
  const currentBirate = file.size / duration;
  const metadata = {
    width,
    height,
    base64,
    duration,
    type: "video",
    byterate: currentBirate,
  } as const;
  file.metadata = { ...metadata, loaded: true };
  return metadata;
}

async function loadAudioDuration(
  file: File,
): Promise<Omit<File["metadata"], "loaded">> {
  if (!file.type.startsWith("audio/")) {
    throw new Error("File is not an audio");
  }
  if (file.metadata.loaded && file.metadata.type === "audio") {
    const { type, base64, duration } = file.metadata;
    return { type, base64, duration };
  }
  const base64 = await getBase64(file);
  if (!base64) {
    throw new Error("Could not read file");
  }
  const fetchAudio = await fetch(base64);
  const { duration } = await getAudioDuration(await fetchAudio.blob());
  const metadata = {
    base64,
    duration,
    type: "audio",
  } as const;
  file.metadata = { ...metadata, loaded: true };
  return metadata;
}

const zipMimeTypes = ["application/zip", "application/x-zip-compressed"];

async function loadZipMetadata(file: File): Promise<File> {
  if (!zipMimeTypes.includes(file.type)) {
    throw new Error("File is not a zip");
  }
  if (file.metadata.loaded && file.metadata.type === "zip") {
    return file;
  }
  const base64 = await getBase64(file);
  if (!base64) {
    throw new Error("Could not read file");
  }
  // get size from name *_{width}x{height}.zip
  const { width = Number.NaN, height = Number.NaN } =
    file.name.match(/(?<width>\d+)x(?<height>\d+)/)?.groups ?? {};
  const metadata = {
    base64,
    type: "zip",
    width: Number(width),
    height: Number(height),
  } as const;
  file.metadata = { ...metadata, loaded: true };
  return file;
}

export async function loadFileMetadata(file: File): Promise<File> {
  if (file.type.startsWith("image/")) {
    await loadImageDimensions(file);
  }
  if (file.type.startsWith("video/")) {
    await loadVideoDuration(file);
  }
  if (file.type.startsWith("audio/")) {
    await loadAudioDuration(file);
  }
  if (zipMimeTypes.includes(file.type)) {
    await loadZipMetadata(file);
  }
  return file;
}

export function loadFileMetadataOnAcceptFiles(
  details: FileUploadFileAcceptDetails,
): Promise<File[]> {
  return Promise.all(details.files.map((file) => loadFileMetadata(file)));
}
